<template>
  <v-container class="summary-step-container">
    <v-row row dense wrap>
      <v-col class="col-6" md6>
        <div class="summary-display-container">
          <label class="application-heading">Alert Applied To</label>
          <div class="hyp-chip-container">
            <div v-if="isWorldWideAOI">
              <v-chip small class="hyp-chip mt-2 ml-2 pa-1 mb-1"
                >World Wide</v-chip
              >
            </div>

            <div v-if="isCustomAOI">
              <v-chip small class="hyp-chip mt-2 ml-2 pa-1 mb-1"
                >Custom Location</v-chip
              >
            </div>

            <div v-if="countries">
              <v-chip
                small
                v-for="(country, index) in countries"
                :key="index"
                class="hyp-chip mt-2 ml-2 pa-1 mb-1"
                >{{ country.properties.displayName }}</v-chip
              >
            </div>

            <div v-if="cities">
              <v-chip
                small
                v-for="(city, index) in cities"
                :key="index"
                class="hyp-chip mt-2 ml-2 pa-1 mb-1"
              >
                {{ city.properties.displayName }},
                {{ city.properties.countryName }}
              </v-chip>
            </div>
          </div>
        </div>

        <div class="summary-display-container">
          <label class="application-heading">Alert Me When</label>
          <div class="hyp-chip-container">
            <v-chip
              v-if="alert.hotspotsEnabled"
              small
              class="hyp-chip pull-left mr-1 ml-2 pa-1 mb-1"
              >Hotspots Trigger</v-chip
            >

            <div v-if="alert.pulseEnabled && pulseDetails">
              <v-chip
                v-for="(item, index) in pulseDetails"
                :key="index"
                small
                class="hyp-chip pull-left mr-1 ml-2 pa-1 mb-1"
              >
                Pulse Trigger for {{ item.name }} below
                {{ item.useLowestDynamicBound ? "average" : item.lowerBound }}
              </v-chip>
            </div>

            <div class="clearfix"></div>
            <div v-if="hasEvents">
              <span class="ml-2 mt-1">These Events trigger</span><br />
              <EventsDisplay v-model="alert.eventsSelection"></EventsDisplay>
            </div>

            <div v-if="hasVerifiedEvents">
              <span class="ml-2 mt-1">These Verified Events trigger</span><br />
              <VerifiedEventsDisplay
                v-model="alert.verifiedEventsSelection"
              ></VerifiedEventsDisplay>
            </div>
          </div>
        </div>

        <div v-if="hasKeywords" class="summary-display-container">
          <KeywordDisplay v-model="alert.keywords"></KeywordDisplay>
        </div>
        <div v-if="!hasVerifiedEvents" class="summary-display-container">
          <SourcesDisplay v-model="alert.sources"></SourcesDisplay>
        </div>
        <div class="summary-display-container">
          <FrequencyDisplay v-model="alert.frequency"></FrequencyDisplay>
        </div>
        <div class="summary-display-container">
          <ScheduleDisplay
            v-model="alert.schedule"
            v-if="alert.schedule && alert.frequency.id == '1440'"
          ></ScheduleDisplay>
        </div>

        <div class="summary-display-container">
          <ExpirationDisplay v-model="alert.expiration"></ExpirationDisplay>
        </div>

        <div class="summary-display-container">
          <SendEmailDisplay v-model="alert.sendEmail"></SendEmailDisplay>
        </div>
        <v-container
          class="summary-display-container share-with-colleagues-display-container pa-0 ma-0"
          v-if="alert.share.type.value && alert.share.type.value !== 'no'"
        >
          <v-row row dense wrap>
            <v-col class="col-12">
              <label class="application-heading">Share with Colleagues</label>
            </v-col>
            <v-col class="col-12 ma-0"> {{ alert.share.type.display }} </v-col>
          </v-row>
        </v-container>
        <UserShareListDisplay v-model="alert.share.list"></UserShareListDisplay>
      </v-col>
      <v-col class="col-6" md6>
        <AoiDisplay v-model="alert.aoiFeature" v-if="isActive"></AoiDisplay>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import lodashGet from "lodash/get";
import lodashIsEqual from "lodash/isEqual";
import lodashOrderBy from "lodash/orderBy";

import {
  validateKeywords,
  validateEvents,
  validateVerifiedEvents,
} from "@/services/alerts/alert.validation.js";
import { WORLD_WIDE_AOI } from "@/common/constants/alert.js";
import { getAllCountries, getAllCities } from "@/common/maps/utilities";

export default {
  name: "SummaryStep",
  components: {
    ExpirationDisplay: () =>
      import("@/components/alerts/wizard/expiration/ExpirationDisplay"),
    KeywordDisplay: () =>
      import("@/components/alerts/wizard/keywords/KeywordDisplay"),
    UserShareListDisplay: () =>
      import("@/components/alerts/wizard/userShareList/UserShareListDisplay"),
    FrequencyDisplay: () =>
      import("@/components/alerts/wizard/frequency/FrequencyDisplay"),
    SourcesDisplay: () =>
      import("@/components/alerts/wizard/sources/SourcesDisplay"),
    EventsDisplay: () =>
      import("@/components/alerts/wizard/events/EventsDisplay"),
    VerifiedEventsDisplay: () =>
      import("@/components/alerts/wizard/verifiedEvents/VerifiedEventsDisplay"),
    ScheduleDisplay: () =>
      import("@/components/alerts/wizard/schedule/ScheduleDisplay"),
    SendEmailDisplay: () =>
      import("@/components/alerts/wizard/sendEmail/SendEmailDisplay"),
    AoiDisplay: () => import("@/components/alerts/wizard/aoi/AoiDisplay"),
  },
  props: {
    alert: {
      type: Object,
      required: true,
    },
    isActive: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      countriesAll: [],
      citiesAll: [],
    };
  },
  computed: {
    hasKeywords() {
      return validateKeywords(this.alert);
    },
    hasEvents() {
      return validateEvents(this.alert);
    },
    hasVerifiedEvents() {
      return validateVerifiedEvents(this.alert);
    },
    isWorldWideAOI() {
      return (
        lodashGet(this.alert, "aoiFeature.properties") &&
        lodashIsEqual(
          this.alert.aoiFeature.properties,
          WORLD_WIDE_AOI.properties
        )
      );
    },
    isCustomAOI() {
      return (
        lodashGet(this.alert, "aoiFeature.properties") &&
        !lodashGet(this.alert, "aoiFeature.properties.aoiIds") &&
        !lodashIsEqual(
          this.alert.aoiFeature.properties,
          WORLD_WIDE_AOI.properties
        )
      );
    },
    countries() {
      const AOI_IDS = lodashGet(this.alert, "aoiFeature.properties.aoiIds");
      let countries = [];
      if (
        AOI_IDS &&
        Array.isArray(AOI_IDS) &&
        AOI_IDS.length &&
        this.countriesAll &&
        Array.isArray(this.countriesAll) &&
        this.countriesAll.length
      ) {
        const LEN = this.countriesAll.length;
        for (let i = 0; i < LEN; i++) {
          const COUNTRY = this.countriesAll[i];
          if (lodashGet(COUNTRY, "properties.iso2")) {
            AOI_IDS.forEach((id) => {
              if (COUNTRY.properties.iso2.toUpperCase() === id.toUpperCase()) {
                countries.push(COUNTRY);
              }
            });
          }
        }
      }
      if (countries.length) {
        countries = lodashOrderBy(
          countries,
          ["properties.displayName"],
          ["asc"]
        );
        return countries;
      }
      return null;
    },
    cities() {
      const AOI_IDS = lodashGet(this.alert, "aoiFeature.properties.aoiIds");
      let cities = [];
      if (
        AOI_IDS &&
        Array.isArray(AOI_IDS) &&
        AOI_IDS.length &&
        this.citiesAll &&
        Array.isArray(this.citiesAll) &&
        this.citiesAll.length
      ) {
        const LEN = this.citiesAll.length;
        for (let i = 0; i < LEN; i++) {
          const CITY = this.citiesAll[i];
          if (lodashGet(CITY, "properties.code")) {
            AOI_IDS.forEach((id) => {
              if (CITY.properties.code.toUpperCase() === id.toUpperCase()) {
                cities.push(CITY);
              }
            });
          }
        }
      }
      if (cities.length) {
        cities = lodashOrderBy(cities, ["properties.displayName"], ["asc"]);
        return cities;
      }
      return null;
    },
    pulseDetails() {
      if (this.alert.pulseDetails) {
        return this.alert.pulseDetails;
      }
      return null;
    },
  },
  async mounted() {
    this.citiesAll = await getAllCities();
    this.countriesAll = await getAllCountries();
  },
  beforeDestroy() {
    this.countriesAll = null;
    this.citiesAll = null;
  },
};
</script>
<style scoped>
.summary-step-container {
  padding: 0;
}
.share-with-colleagues-display-container {
  padding: 0;
}
.summary-display-container {
  margin: 15px 0;
}
.hyp-chip >>> .v-chip__content {
  height: 24px;
  border-radius: 16px;
  padding: 0 6px;
}
.hyp-chip-container {
  width: 100%;
}
label {
  font-size: 16px;
}
</style>
